import { compareSemver } from "./utils/semver.js";
export const ENTERPRISE_PLUGIN_VERSION_GATE_CAPABILITY = "1";
export const CATALYST_JUGUANG_MIN_DOSIA_VERSION = "0.3.69";
/**
 * 与 `plugins/dosia-projects/.claude-plugin/dosia.json` 的 `minDosiaVersion`
 * 逐字一致（该 manifest 是插件自己声明的版本底线，这里不另定一个数）。
 * 相等关系由 enterprise-plugin-version-gate.test.ts 对着 manifest 实读钉住。
 */
export const DOSIA_PROJECTS_MIN_DOSIA_VERSION = "0.3.84";
/**
 * 与 `plugins/dosia-relay/.claude-plugin/dosia.json` 的 `minDosiaVersion`
 * 逐字一致（同上：manifest 是单源，这里不另定一个数）。
 *
 * 取值依据（REQ-220 PR-3b）：本 PR **删掉**九个 leaf 的进程内壳，所以底线必须是
 * 「第一个不再自带这些壳的桌面版本」，而不是「第一个会签中继身份 token 的版本」
 * （那是 0.3.90，PR-1 #2998 已随 v0.3.90 #3009 发版）。给 0.3.90 及更早的客户端
 * 下发这个插件，会让它同时拥有 `dosia-tools` 的进程内同名 leaf 和
 * `dosia-relay` 的远程 leaf —— 去重靠 `markHostOwnedMcpConnections` 按 **MCP server
 * 名**匹配，`"dosia-tools" ≠ "dosia-relay"` 必然不命中（见 builtin-seed-sync.ts
 * 里 dosia-projects 那段同因裁决），结果是员工一次看见两套同名工具。
 * 根 `package.json` 现为 0.3.90（已发版），故本 PR 所在的下一个补丁版本 = 0.3.91。
 */
export const DOSIA_RELAY_MIN_DOSIA_VERSION = "0.3.91";
const PROTECTED_ENTERPRISE_PLUGIN_POLICIES = {
    "catalyst-juguang": {
        minDosiaVersion: CATALYST_JUGUANG_MIN_DOSIA_VERSION,
        gateCapability: ENTERPRISE_PLUGIN_VERSION_GATE_CAPABILITY,
    },
    // REQ-107：dosia-projects 自本批起经 L5 下发到员工桌面（`DEFAULT_SEED_PLUGINS`），
    // 与 catalyst-juguang 同形态 —— 旧客户端没有本地 manifest 预检，必须由服务端
    // 分发门按 minDosiaVersion + gateCapability 挡住，否则老版本装上一个它认不出
    // 令牌注入声明的连接器。
    "dosia-projects": {
        minDosiaVersion: DOSIA_PROJECTS_MIN_DOSIA_VERSION,
        gateCapability: ENTERPRISE_PLUGIN_VERSION_GATE_CAPABILITY,
    },
    // REQ-220 PR-3b：dosia-relay 自本批起经 L5 下发到员工桌面
    // （`DEFAULT_SEED_PLUGINS`），与上面两条同形态。这道门在这里额外承担一件事：
    // 挡住**还带着进程内壳**的旧客户端（≤0.3.90），否则同一批 leaf 名会同时来自
    // `dosia-tools` 和 `dosia-relay` 两个 server（见 DOSIA_RELAY_MIN_DOSIA_VERSION）。
    "dosia-relay": {
        minDosiaVersion: DOSIA_RELAY_MIN_DOSIA_VERSION,
        gateCapability: ENTERPRISE_PLUGIN_VERSION_GATE_CAPABILITY,
    },
};
export const PROTECTED_ENTERPRISE_PLUGIN_NAMES = Object.freeze(Object.keys(PROTECTED_ENTERPRISE_PLUGIN_POLICIES));
/**
 * New Enterprise plugins that depend on a minimum DOSIA runtime must not be
 * treated as legacy when their version manifest is missing.
 */
export function requiresEnterprisePluginDosiaManifest(pluginName) {
    return Object.hasOwn(PROTECTED_ENTERPRISE_PLUGIN_POLICIES, pluginName);
}
/**
 * Parse and enforce an Enterprise-managed plugin's `.claude-plugin/dosia.json`
 * before any of that plugin's files are materialized or registered.
 *
 * Legacy manifests without `minDosiaVersion` stay compatible. Once a minimum
 * is declared, malformed JSON or an unparseable current/minimum version fails
 * closed, matching Skill Hub's `assertMinVersion` posture.
 */
export function evaluateEnterprisePluginDosiaManifest(input) {
    let parsed;
    try {
        parsed = JSON.parse(input.manifestText);
    }
    catch {
        return { allowed: false, reason: "invalid_manifest" };
    }
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
        return { allowed: false, reason: "invalid_manifest" };
    }
    const manifest = parsed;
    const protectedPlugin = requiresEnterprisePluginDosiaManifest(input.pluginName);
    if (protectedPlugin && manifest.name !== input.pluginName) {
        return { allowed: false, reason: "plugin_name_mismatch" };
    }
    const minValue = manifest.minDosiaVersion;
    if (minValue === undefined) {
        return protectedPlugin
            ? { allowed: false, reason: "missing_min_dosia_version" }
            : { allowed: true };
    }
    if (typeof minValue !== "string"
        || !minValue
        || compareSemver(minValue, minValue) === null) {
        return {
            allowed: false,
            minDosiaVersion: typeof minValue === "string" ? minValue : undefined,
            reason: "invalid_min_dosia_version",
        };
    }
    if (compareSemver(input.currentDosiaVersion, input.currentDosiaVersion) === null) {
        return {
            allowed: false,
            minDosiaVersion: minValue,
            reason: "invalid_current_dosia_version",
        };
    }
    return compareSemver(input.currentDosiaVersion, minValue) < 0
        ? { allowed: false, minDosiaVersion: minValue, reason: "requires_newer_dosia" }
        : { allowed: true, minDosiaVersion: minValue };
}
/**
 * Server-side distribution gate for protected Enterprise plugins.
 *
 * Old clients do not contain the local manifest preflight, so merely shipping
 * a minDosiaVersion manifest is insufficient. A client must explicitly
 * advertise the gate capability and a parseable product version at or above
 * the protected plugin's minimum before the server includes that plugin in the
 * file manifest. Unrelated legacy plugins remain untouched.
 */
export function evaluateEnterprisePluginDistribution(input) {
    if (!requiresEnterprisePluginDosiaManifest(input.pluginName)) {
        return { allowed: true };
    }
    const policy = PROTECTED_ENTERPRISE_PLUGIN_POLICIES[input.pluginName];
    if (input.gateCapability !== policy.gateCapability) {
        return { allowed: false, reason: "missing_gate_capability" };
    }
    if (!input.currentDosiaVersion
        || compareSemver(input.currentDosiaVersion, input.currentDosiaVersion) === null) {
        return { allowed: false, reason: "invalid_current_dosia_version" };
    }
    return compareSemver(input.currentDosiaVersion, policy.minDosiaVersion) < 0
        ? { allowed: false, reason: "requires_newer_dosia" }
        : { allowed: true };
}
//# sourceMappingURL=enterprise-plugin-version-gate.js.map